/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/root.reducers';
import {
    GRID_TIMEBAR_REFRESH_TIME,
    IDLE_GRID_GUIDE_DURATION_IN_SECOND,
    MILLISECONDS_IN_A_SECOND,
} from '../../../config';

import './TimeBar.scss';

const TimeBar = (): JSX.Element => {
    const timebarRef = useRef<HTMLDivElement>(null);
    const timebarHeadRef = useRef(new Image());
    const { isPastGuideMode, startTime, currentTimestamp } = useSelector(
        (state: RootState) => state.gridGuide,
    );
    const [timeBarPosition, setTimeBarPosition] = useState(0);

    /**
     * Calculates and sets the current position of the timebar as a percentage of the timebar width.
     *
     * @param pixelPerSecond - Number of pixels the time bar should move per second.
     * @param extraDistance - Extra distance (in px) to account for the timebar head and margin.
     *
     * The function determines the elapsed time in seconds since the currentTimestamp,
     * calculates the pixel position, and then converts it to a percentage of the timebar's width,
     * adjusting for extraDistance. The resulting percentage is clamped to a minimum of 0 and
     * used to update the time bar's position state.
     */

    const evaluateCurrentPosition = (pixelPerSecond: number, extraDistance: number) => {
        if (!currentTimestamp) return;
        const currentTime = new Date();
        const seconds =
            (currentTime.getTime() - currentTimestamp?.getTime()) / MILLISECONDS_IN_A_SECOND;
        const position = Number(pixelPerSecond * seconds);
        // Use the actual timebar width for percentage calculation
        const timebarWidth = timebarRef.current?.offsetWidth ?? 1; // prevent division by zero
        const calcPosition = (position / timebarWidth) * 100 - (extraDistance / timebarWidth) * 100;
        setTimeBarPosition(Math.max(0, calcPosition));
    };

    useEffect(() => {
        let pixelPerSecond = 0;
        let extraDistance = 0;
        if (timebarHeadRef.current) {
            extraDistance =
                (timebarHeadRef.current?.offsetWidth || 0) / 2 +
                parseFloat(getComputedStyle(timebarHeadRef.current).marginLeft);
        }
        if (timebarRef.current) {
            pixelPerSecond =
                (timebarRef.current?.offsetWidth - extraDistance) /
                IDLE_GRID_GUIDE_DURATION_IN_SECOND;
        }

        evaluateCurrentPosition(pixelPerSecond, extraDistance);

        const timeoutId = setInterval(() => {
            evaluateCurrentPosition(pixelPerSecond, extraDistance);
        }, GRID_TIMEBAR_REFRESH_TIME);
        return () => {
            clearInterval(timeoutId);
        };
    }, [startTime, currentTimestamp]);

    return (
        <React.Fragment>
            {isPastGuideMode ? (
                <div className="timebar-container past-guide-mode">
                    <div className="timebar-past-guide-mode"></div>
                </div>
            ) : (
                <div className="timebar-container" ref={timebarRef}>
                    <div
                        className="timebar"
                        style={{ width: `${timeBarPosition}%` }}
                        data-testid="guide-timebar"
                    ></div>
                    <img className="timebar-head" ref={timebarHeadRef} alt=""></img>
                </div>
            )}
        </React.Fragment>
    );
};

export default TimeBar;
